/**
 * Closed runtime parser for the imported MinbarLive pilot view.
 *
 * The backend validates the same contract, but the browser must not render a shape,
 * identity, total or citation it cannot prove. `null` means "refuse the whole report";
 * callers show a generic safe state and never a partial payload.
 */

export const MINBAR_PILOT_PROJECT_ID = "prj_minbarlive";
export const MINBAR_PILOT_SCHEMA_VERSION = "minbar_pilot.view.v1";
export const MINBAR_PILOT_CODE_COMMIT = "c658ceb";
export const MINBAR_PILOT_VIEW_IMPLEMENTATION = "pr26";
export const MINBAR_PILOT_REQUESTED_MODEL = "deepseek-flash";
const ALLOWED_RETURNED_MODELS = new Set([
  "deepseek-flash",
  "DeepSeek-V4.1-Flash-1017",
]);

export type PilotCallState = "completed" | "rejected" | "unknown" | "failed" | "not_called";
export type PilotAnalysisState = "accepted" | "refused" | "unknown" | "failed" | "not_called";
export type PilotEvidenceState = "source_backed" | "no_usable_page" | "not_applicable";
export type PilotFitState = "inference" | "unknown";

export interface PilotPage {
  source_id: string;
  url: string;
  fetched_at: string;
  content_hash: string;
  title: string | null;
}

export interface PilotEvidence {
  state: PilotEvidenceState;
  fetched_page_count: number;
  model_supplied_source_count: number;
  model_omitted_source_count: number;
  model_supplied_source_ids: string[];
  model_omitted_source_ids: string[];
  pages: PilotPage[];
}

export interface PilotClaim {
  field: string;
  value: string;
}

export interface PilotObservation {
  text: string;
  source_id: string;
  content_hash: string;
}

export interface PilotBusinessFit {
  state: PilotFitState;
  level: "strong" | "moderate" | "weak" | null;
  rationale: string | null;
}

export interface PilotRefusal {
  code: "analysis_invalid_input" | "analysis_unsupported_prior_fact";
  root_key_detail: "unknown";
  claim_detail: "not_retained" | null;
}

export interface PilotAnalysis {
  state: PilotAnalysisState;
  business_fit: PilotBusinessFit;
  new_observations: PilotObservation[];
  missing_information: string[];
  next_review: string[];
  refusal: PilotRefusal | null;
}

export interface PilotUsage {
  prompt_tokens: number | null;
  cache_hit_tokens: number | null;
  cache_miss_tokens: number | null;
  completion_tokens: number | null;
  reasoning_tokens: number | null;
}

export interface PilotCall {
  state: PilotCallState;
  requested_model: string;
  actual_model: string | null;
  error_code: string | null;
  response_sha256: string | null;
  usage: PilotUsage;
  estimated_micros: number | null;
}

export interface PilotRow {
  row_number: number;
  row_id: string;
  organization_id: string;
  organization_name: string;
  association: "parent_approved_not_independently_verified" | "unresolved";
  unverified_prior_claims: PilotClaim[];
  evidence: PilotEvidence;
  presentation: {
    summary: string | null;
    omitted_content_count: number;
  };
  analysis: PilotAnalysis;
  call: PilotCall;
}

export interface PilotSummary {
  requested: number;
  attempted: number;
  accepted: number;
  refused: number;
  calls_unknown: number;
  calls_failed: number;
  not_called: number;
  accepted_source_backed: number;
  accepted_no_usable_page: number;
  usage: PilotUsage;
  cost: {
    currency: "USD";
    estimated_micros: number;
    unknown_reserved_micros: number;
    unknown_cost_rows: number;
    basis: "estimated_from_reported_usage_peak_pricing";
    is_invoice: false;
  };
}

export interface PilotReportIdentity {
  report_id: string;
  pilot_id: string;
  project_id: "prj_minbarlive";
  project_slug: "minbarlive-growth";
  origin: {
    kind: "imported_cli_pilot";
    live_job: false;
    new_run: false;
    code_commit: "c658ceb";
    input_sha256: string;
    state_sha256: string;
    rows_sha256: string;
    result_sha256: string;
  };
  view: {
    implementation: "pr26";
    schema_version: "minbar_pilot.view.v1";
  };
}

export interface PilotAvailable {
  schema_version: "minbar_pilot.view.v1";
  state: "available";
  report: PilotReportIdentity;
  timing: {
    pilot_finished_at: string;
    view_prepared_at: string;
  };
  model: {
    requested: "deepseek-flash";
    returned: string[];
  };
  human_acceptance: {
    state: "pending";
    accepted_means: "schema_and_source_citation_checks_only";
  };
  provenance: {
    kind: "parent_curated_organizational_summary";
    source: "existing_pilot_responses";
    omitted_content_count: number;
    notice: string;
  };
  summary: PilotSummary;
  rows: PilotRow[];
  limitations: string[];
}

export interface PilotUnavailable {
  schema_version: "minbar_pilot.view.v1";
  state: "unavailable";
  reason: "not_configured";
}

export type PilotResult = PilotAvailable | PilotUnavailable;

const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;
const RAW_HTML = /<\s*(?:!|\/?\s*[a-z][^>]*)>|<!--/i;
const DIGEST = /^sha256:[0-9a-f]{64}$/;
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const FIELD_NAME = /^[a-z][a-z0-9_]{0,63}$/;
const ERROR_CODE = /^[a-z][a-z0-9_]{1,63}$/;
const CONTACT_ACTION =
  /\b(?:contact|e-?mail|phone|call|message|outreach|whatsapp|kontakt|telefon|nazov|po[sš]alj|poruk)\w*/i;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PHONE = /(?<!\d)(?:\+\d{1,3}[\s./-]?)?(?:\d[\s./-]?){8,14}\d(?!\d)/;
const SOCIAL_HANDLE = /(?<![\w.])@[A-Za-z0-9_.]{3,}/;
const HONORIFIC = /\b(?:imam|ef\.?|mr\.?|mrs\.?|ms\.?|dr\.?)\s+[A-Z][a-z]{2,}/;
const PERSON_LEVEL_FIELDS = new Set([
  "email",
  "email_address",
  "phone",
  "phone_number",
  "mobile",
  "whatsapp",
  "contact_name",
  "person_name",
  "first_name",
  "last_name",
  "full_name",
  "imam_name",
  "address_line",
]);

const CALL_STATES = new Set<PilotCallState>([
  "completed", "rejected", "unknown", "failed", "not_called",
]);
const ANALYSIS_STATES = new Set<PilotAnalysisState>([
  "accepted", "refused", "unknown", "failed", "not_called",
]);
const EVIDENCE_STATES = new Set<PilotEvidenceState>([
  "source_backed", "no_usable_page", "not_applicable",
]);
const FIT_LEVELS = new Set(["strong", "moderate", "weak"]);
const CALL_TO_ANALYSIS: Record<PilotCallState, PilotAnalysisState> = {
  completed: "accepted",
  rejected: "refused",
  unknown: "unknown",
  failed: "failed",
  not_called: "not_called",
};

const TOP_KEYS = [
  "schema_version", "state", "report", "timing", "model",
  "human_acceptance", "provenance", "summary", "rows", "limitations",
];
const REPORT_KEYS = [
  "report_id", "pilot_id", "project_id", "project_slug", "origin", "view",
];
const ORIGIN_KEYS = [
  "kind", "live_job", "new_run", "code_commit",
  "input_sha256", "state_sha256", "rows_sha256", "result_sha256",
];
const VIEW_KEYS = ["implementation", "schema_version"];
const TIMING_KEYS = ["pilot_finished_at", "view_prepared_at"];
const MODEL_KEYS = ["requested", "returned"];
const ACCEPTANCE_KEYS = ["state", "accepted_means"];
const PROVENANCE_KEYS = [
  "kind", "source", "omitted_content_count", "notice",
];
const SUMMARY_KEYS = [
  "requested", "attempted", "accepted", "refused", "calls_unknown",
  "calls_failed", "not_called", "accepted_source_backed",
  "accepted_no_usable_page", "usage", "cost",
];
const USAGE_KEYS = [
  "prompt_tokens", "cache_hit_tokens", "cache_miss_tokens",
  "completion_tokens", "reasoning_tokens",
];
const COST_KEYS = [
  "currency", "estimated_micros", "unknown_reserved_micros",
  "unknown_cost_rows", "basis", "is_invoice",
];
const ROW_KEYS = [
  "row_number", "row_id", "organization_id", "organization_name", "association",
  "unverified_prior_claims", "evidence", "presentation", "analysis", "call",
];
const EVIDENCE_KEYS = [
  "state", "fetched_page_count", "model_supplied_source_count",
  "model_omitted_source_count", "model_supplied_source_ids",
  "model_omitted_source_ids", "pages",
];
const PAGE_KEYS = ["source_id", "url", "fetched_at", "content_hash", "title"];
const PRESENTATION_KEYS = ["summary", "omitted_content_count"];
const ANALYSIS_KEYS = [
  "state", "business_fit", "new_observations", "missing_information",
  "next_review", "refusal",
];
const FIT_KEYS = ["state", "level", "rationale"];
const OBSERVATION_KEYS = ["text", "source_id", "content_hash"];
const REFUSAL_KEYS = ["code", "root_key_detail", "claim_detail"];
const CALL_KEYS = [
  "state", "requested_model", "actual_model", "error_code",
  "response_sha256", "usage", "estimated_micros",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function exact(value: unknown, keys: string[]): value is Record<string, unknown> {
  return (
    isRecord(value)
    && Object.keys(value).length === keys.length
    && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  );
}

function isInt(value: unknown, maximum: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= maximum;
}

function nullableInt(value: unknown, maximum: number): number | null | undefined {
  if (value === null) return null;
  return isInt(value, maximum) ? value : undefined;
}

function identifier(value: unknown): string | null {
  return typeof value === "string" && IDENTIFIER.test(value) ? value : null;
}

function text(value: unknown, maximum: number, allowEmpty = false): string | null {
  if (typeof value !== "string" || value.length > maximum || (!allowEmpty && !value)) {
    return null;
  }
  if (CONTROL.test(value) || RAW_HTML.test(value)) return null;
  if (EMAIL.test(value) || PHONE.test(value) || SOCIAL_HANDLE.test(value) || HONORIFIC.test(value)) {
    return null;
  }
  return value;
}

function textOrNull(value: unknown, maximum: number): string | null | undefined {
  if (value === null) return null;
  const parsed = text(value, maximum);
  return parsed ?? undefined;
}

function digest(value: unknown): string | null {
  return typeof value === "string" && DIGEST.test(value) ? value : null;
}

function timestamp(value: unknown): string | null {
  if (typeof value !== "string" || !TIMESTAMP.test(value)) return null;
  return Number.isFinite(Date.parse(value)) ? value : null;
}

function safeHttpUrl(value: unknown): string | null {
  if (
    typeof value !== "string"
    || !value
    || value.length > 2048
    || CONTROL.test(value)
    || /\s/.test(value)
    || /[\\<>]/.test(value)
  ) {
    return null;
  }
  try {
    const parsed = new URL(value);
    if (!["http:", "https:"].includes(parsed.protocol) || !parsed.hostname) return null;
    if (parsed.username || parsed.password) return null;
    return value;
  } catch {
    return null;
  }
}

function list(value: unknown, maximum: number): unknown[] | null {
  return Array.isArray(value) && value.length <= maximum ? value : null;
}

function stringList(
  value: unknown,
  maximum: number,
  itemMaximum: number,
  rejectContactAction = false,
): string[] | null {
  const raw = list(value, maximum);
  if (raw === null) return null;
  const items: string[] = [];
  for (const item of raw) {
    const parsed = text(item, itemMaximum);
    if (parsed === null) return null;
    if (rejectContactAction && CONTACT_ACTION.test(parsed)) return null;
    items.push(parsed);
  }
  return new Set(items).size === items.length ? items : null;
}

function parseClaim(value: unknown): PilotClaim | null {
  if (!exact(value, ["field", "value"])) return null;
  const field = value.field;
  if (
    typeof field !== "string"
    || !FIELD_NAME.test(field)
    || PERSON_LEVEL_FIELDS.has(field)
  ) {
    return null;
  }
  const claimValue = text(value.value, 512);
  return claimValue === null ? null : { field, value: claimValue };
}

function parsePage(value: unknown): PilotPage | null {
  if (!exact(value, PAGE_KEYS)) return null;
  const sourceId = identifier(value.source_id);
  const url = safeHttpUrl(value.url);
  const fetchedAt = timestamp(value.fetched_at);
  const contentHash = digest(value.content_hash);
  const title = textOrNull(value.title, 300);
  if (sourceId === null || url === null || fetchedAt === null
      || contentHash === null || title === undefined) {
    return null;
  }
  return {
    source_id: sourceId,
    url,
    fetched_at: fetchedAt,
    content_hash: contentHash,
    title,
  };
}

function parseFit(value: unknown): PilotBusinessFit | null {
  if (!exact(value, FIT_KEYS)) return null;
  if (value.state === "unknown") {
    return value.level === null && value.rationale === null
      ? { state: "unknown", level: null, rationale: null }
      : null;
  }
  if (value.state !== "inference") return null;
  const level = value.level;
  const rationale = text(value.rationale, 800);
  if (typeof level !== "string" || !FIT_LEVELS.has(level) || rationale === null) return null;
  return {
    state: "inference",
    level: level as PilotBusinessFit["level"],
    rationale,
  };
}

function parseRefusal(value: unknown): PilotRefusal | null | undefined {
  if (value === null) return null;
  if (!exact(value, REFUSAL_KEYS)) return undefined;
  const code = value.code;
  if (code !== "analysis_invalid_input" && code !== "analysis_unsupported_prior_fact") {
    return undefined;
  }
  if (value.root_key_detail !== "unknown") return undefined;
  if (code === "analysis_invalid_input") {
    if (value.claim_detail !== null) return undefined;
  } else if (value.claim_detail !== "not_retained") {
    return undefined;
  }
  return {
    code,
    root_key_detail: "unknown",
    claim_detail: code === "analysis_invalid_input" ? null : "not_retained",
  };
}

function parseObservation(value: unknown): PilotObservation | null {
  if (!exact(value, OBSERVATION_KEYS)) return null;
  const textValue = text(value.text, 2000);
  const sourceId = identifier(value.source_id);
  const contentHash = digest(value.content_hash);
  if (textValue === null || sourceId === null || contentHash === null) return null;
  return { text: textValue, source_id: sourceId, content_hash: contentHash };
}

function parseAnalysis(value: unknown): PilotAnalysis | null {
  if (!exact(value, ANALYSIS_KEYS)) return null;
  const state = value.state;
  if (typeof state !== "string" || !ANALYSIS_STATES.has(state as PilotAnalysisState)) {
    return null;
  }
  const fit = parseFit(value.business_fit);
  const rawObservations = list(value.new_observations, 12);
  const missing = stringList(value.missing_information, 8, 500);
  const nextReview = stringList(value.next_review, 8, 500, true);
  const refusal = parseRefusal(value.refusal);
  if (fit === null || rawObservations === null || missing === null
      || nextReview === null || refusal === undefined) {
    return null;
  }
  const observations: PilotObservation[] = [];
  for (const raw of rawObservations) {
    const observation = parseObservation(raw);
    if (observation === null) return null;
    observations.push(observation);
  }
  return {
    state: state as PilotAnalysisState,
    business_fit: fit,
    new_observations: observations,
    missing_information: missing,
    next_review: nextReview,
    refusal,
  };
}

function parseUsage(value: unknown): PilotUsage | null {
  if (!exact(value, USAGE_KEYS)) return null;
  const promptTokens = nullableInt(value.prompt_tokens, 1_000_000_000);
  const cacheHitTokens = nullableInt(value.cache_hit_tokens, 1_000_000_000);
  const cacheMissTokens = nullableInt(value.cache_miss_tokens, 1_000_000_000);
  const completionTokens = nullableInt(value.completion_tokens, 1_000_000_000);
  const reasoningTokens = nullableInt(value.reasoning_tokens, 1_000_000_000);
  if (
    promptTokens === undefined
    || cacheHitTokens === undefined
    || cacheMissTokens === undefined
    || completionTokens === undefined
    || reasoningTokens === undefined
  ) {
    return null;
  }
  if (promptTokens !== null && cacheHitTokens !== null && cacheMissTokens !== null
      && cacheHitTokens + cacheMissTokens !== promptTokens) {
    return null;
  }
  if (completionTokens !== null && reasoningTokens !== null
      && reasoningTokens > completionTokens) {
    return null;
  }
  return {
    prompt_tokens: promptTokens,
    cache_hit_tokens: cacheHitTokens,
    cache_miss_tokens: cacheMissTokens,
    completion_tokens: completionTokens,
    reasoning_tokens: reasoningTokens,
  };
}

function parseCall(value: unknown): PilotCall | null {
  if (!exact(value, CALL_KEYS)) return null;
  const state = value.state;
  if (typeof state !== "string" || !CALL_STATES.has(state as PilotCallState)) return null;
  if (value.requested_model !== MINBAR_PILOT_REQUESTED_MODEL) return null;
  const actualModel = value.actual_model === null ? null : value.actual_model;
  if (actualModel !== null
      && (typeof actualModel !== "string" || !ALLOWED_RETURNED_MODELS.has(actualModel))) {
    return null;
  }
  const errorCode = value.error_code === null ? null : value.error_code;
  if (errorCode !== null
      && (typeof errorCode !== "string" || !ERROR_CODE.test(errorCode))) {
    return null;
  }
  const responseSha = value.response_sha256 === null ? null : digest(value.response_sha256);
  if (value.response_sha256 !== null && responseSha === null) return null;
  const usage = parseUsage(value.usage);
  const estimated = nullableInt(value.estimated_micros, 1_000_000_000);
  if (usage === null || estimated === undefined) return null;

  const knownUsage = (
    usage.prompt_tokens !== null
    && usage.cache_hit_tokens !== null
    && usage.cache_miss_tokens !== null
    && usage.completion_tokens !== null
  );
  if ((state === "completed" || state === "rejected")
      && (!knownUsage || estimated === null)) {
    return null;
  }
  if (state === "completed" && (
    actualModel === null || responseSha === null || errorCode !== null
  )) {
    return null;
  }
  if (state === "rejected" && errorCode === null) return null;
  if ((state === "unknown" || state === "failed") && errorCode === null) return null;
  if (state === "not_called" && (
    errorCode !== null
    || actualModel !== null
    || responseSha !== null
    || estimated !== null
    || Object.values(usage).some((item) => item !== null)
  )) {
    return null;
  }
  return {
    state: state as PilotCallState,
    requested_model: MINBAR_PILOT_REQUESTED_MODEL,
    actual_model: actualModel,
    error_code: errorCode,
    response_sha256: responseSha,
    usage,
    estimated_micros: estimated,
  };
}

function parseEvidence(value: unknown): PilotEvidence | null {
  if (!exact(value, EVIDENCE_KEYS)) return null;
  const state = value.state;
  if (typeof state !== "string" || !EVIDENCE_STATES.has(state as PilotEvidenceState)) {
    return null;
  }
  const rawPages = list(value.pages, 5);
  const rawSupplied = list(value.model_supplied_source_ids, 5);
  const rawOmitted = list(value.model_omitted_source_ids, 5);
  if (rawPages === null || rawSupplied === null || rawOmitted === null) return null;
  const pages: PilotPage[] = [];
  for (const raw of rawPages) {
    const page = parsePage(raw);
    if (page === null) return null;
    pages.push(page);
  }
  const supplied: string[] = [];
  for (const raw of rawSupplied) {
    const sourceId = identifier(raw);
    if (sourceId === null) return null;
    supplied.push(sourceId);
  }
  const omitted: string[] = [];
  for (const raw of rawOmitted) {
    const sourceId = identifier(raw);
    if (sourceId === null) return null;
    omitted.push(sourceId);
  }
  const sourceIds = new Set(pages.map((page) => page.source_id));
  if (sourceIds.size !== pages.length) return null;
  if (new Set(supplied).size !== supplied.length || new Set(omitted).size !== omitted.length) {
    return null;
  }
  const suppliedSet = new Set(supplied);
  const omittedSet = new Set(omitted);
  if ([...suppliedSet].some((item) => omittedSet.has(item))) return null;
  if (new Set([...supplied, ...omitted]).size !== sourceIds.size
      || [...sourceIds].some((item) => !suppliedSet.has(item) && !omittedSet.has(item))) {
    return null;
  }
  if (
    !isInt(value.fetched_page_count, 5)
    || value.fetched_page_count !== pages.length
    || !isInt(value.model_supplied_source_count, 5)
    || value.model_supplied_source_count !== supplied.length
    || !isInt(value.model_omitted_source_count, 5)
    || value.model_omitted_source_count !== omitted.length
  ) {
    return null;
  }
  return {
    state: state as PilotEvidenceState,
    fetched_page_count: pages.length,
    model_supplied_source_count: supplied.length,
    model_omitted_source_count: omitted.length,
    model_supplied_source_ids: supplied,
    model_omitted_source_ids: omitted,
    pages,
  };
}

function expectedEvidenceState(
  evidence: PilotEvidence,
  callState: PilotCallState,
): PilotEvidenceState {
  // Evidence availability is independent of acceptance: a refused/unknown/failed
  // call may still have really supplied pages. It never becomes an accepted analysis.
  if (evidence.model_supplied_source_ids.length > 0) return "source_backed";
  if (callState === "not_called") return "not_applicable";
  if (callState === "completed" || callState === "rejected") return "no_usable_page";
  return evidence.pages.length > 0 || evidence.model_omitted_source_ids.length > 0
    ? "no_usable_page"
    : "not_applicable";
}

function parseRow(value: unknown, expectedRowNumber: number): PilotRow | null {
  if (!exact(value, ROW_KEYS)) return null;
  if (!isInt(value.row_number, 11) || value.row_number !== expectedRowNumber) return null;
  const rowId = identifier(value.row_id);
  const organizationId = identifier(value.organization_id);
  const organizationName = text(value.organization_name, 200);
  const association = value.association;
  if (
    rowId === null || organizationId === null || organizationName === null
    || (association !== "parent_approved_not_independently_verified"
      && association !== "unresolved")
  ) {
    return null;
  }
  const rawClaims = list(value.unverified_prior_claims, 10);
  if (rawClaims === null) return null;
  const claims: PilotClaim[] = [];
  for (const raw of rawClaims) {
    const claim = parseClaim(raw);
    if (claim === null) return null;
    claims.push(claim);
  }
  const evidence = parseEvidence(value.evidence);
  if (!exact(value.presentation, PRESENTATION_KEYS)) return null;
  const summary = textOrNull(value.presentation.summary, 2000);
  const omittedCount = nullableInt(value.presentation.omitted_content_count, 100_000);
  const analysis = parseAnalysis(value.analysis);
  const call = parseCall(value.call);
  if (
    evidence === null || summary === undefined
    || omittedCount === null || omittedCount === undefined
    || analysis === null || call === null
  ) {
    return null;
  }
  const supplied = new Set(evidence.model_supplied_source_ids);
  if (evidence.state !== expectedEvidenceState(evidence, call.state)) return null;
  if (call.state === "not_called" && (
    evidence.pages.length > 0
    || evidence.model_supplied_source_ids.length > 0
    || evidence.model_omitted_source_ids.length > 0
  )) {
    return null;
  }
  const pageById = new Map(evidence.pages.map((page) => [page.source_id, page]));
  for (const observation of analysis.new_observations) {
    const page = pageById.get(observation.source_id);
    if (page === undefined
        || !supplied.has(observation.source_id)
        || page.content_hash !== observation.content_hash) {
      return null;
    }
  }
  if (CALL_TO_ANALYSIS[call.state] !== analysis.state) return null;

  if (analysis.state === "accepted") {
    if (call.state !== "completed" || call.error_code !== null) return null;
    if (summary === null) return null;
    if (evidence.state === "source_backed" && analysis.new_observations.length === 0) {
      return null;
    }
    if (evidence.state === "no_usable_page"
        && (analysis.new_observations.length > 0
          || analysis.business_fit.state !== "unknown")) {
      return null;
    }
    if (analysis.business_fit.state === "inference"
        && (evidence.state !== "source_backed" || analysis.new_observations.length === 0)) {
      return null;
    }
    if (analysis.refusal !== null) return null;
  } else {
    if (summary !== null) return null;
    if (analysis.new_observations.length > 0
        || analysis.business_fit.state !== "unknown"
        || analysis.missing_information.length > 0
        || analysis.next_review.length > 0) {
      return null;
    }
    if (analysis.state === "refused") {
      if (
        analysis.refusal === null
        || call.state !== "rejected"
        || call.error_code !== analysis.refusal.code
      ) {
        return null;
      }
    } else if (analysis.refusal !== null) {
      return null;
    }
  }

  return {
    row_number: expectedRowNumber,
    row_id: rowId,
    organization_id: organizationId,
    organization_name: organizationName,
    association,
    unverified_prior_claims: claims,
    evidence,
    presentation: { summary, omitted_content_count: omittedCount },
    analysis,
    call,
  };
}

function usageTotal(rows: PilotRow[], key: keyof PilotUsage): number | null {
  const values = rows.map((row) => row.call.usage[key]);
  if (values.some((value) => value === null)) return null;
  return values.reduce<number>((total, value) => total + (value ?? 0), 0);
}

function parseSummary(
  value: unknown,
  rows: PilotRow[],
  omittedTotal: number,
): PilotSummary | null {
  if (!exact(value, SUMMARY_KEYS)) return null;
  const calls = rows.map((row) => row.call.state);
  const accepted = rows.filter((row) => row.analysis.state === "accepted");
  const expected = {
    requested: rows.length,
    attempted: calls.filter((state) => state !== "not_called").length,
    accepted: calls.filter((state) => state === "completed").length,
    refused: calls.filter((state) => state === "rejected").length,
    calls_unknown: calls.filter((state) => state === "unknown").length,
    calls_failed: calls.filter((state) => state === "failed").length,
    not_called: calls.filter((state) => state === "not_called").length,
    accepted_source_backed: accepted.filter((row) => row.evidence.state === "source_backed").length,
    accepted_no_usable_page: accepted.filter((row) => row.evidence.state === "no_usable_page").length,
  };
  if (rows.length !== 10) return null;
  for (const key of Object.keys(expected) as (keyof typeof expected)[]) {
    if (!isInt(value[key], 10) || value[key] !== expected[key]) return null;
  }
  if (omittedTotal < 0) return null;

  if (!exact(value.usage, USAGE_KEYS)) return null;
  const usage: PilotUsage = {
    prompt_tokens: usageTotal(rows, "prompt_tokens"),
    cache_hit_tokens: usageTotal(rows, "cache_hit_tokens"),
    cache_miss_tokens: usageTotal(rows, "cache_miss_tokens"),
    completion_tokens: usageTotal(rows, "completion_tokens"),
    reasoning_tokens: usageTotal(rows, "reasoning_tokens"),
  };
  for (const key of USAGE_KEYS as (keyof PilotUsage)[]) {
    const stored = nullableInt(value.usage[key], 10_000_000_000);
    if (stored === undefined || stored !== usage[key]) return null;
  }

  if (!exact(value.cost, COST_KEYS)) return null;
  const knownCosts = rows
    .map((row) => row.call.estimated_micros)
    .filter((item): item is number => item !== null);
  const unknownCostRows = rows.length - knownCosts.length;
  const estimate = knownCosts.reduce((total, item) => total + item, 0);
  if (
    value.cost.currency !== "USD"
    || !isInt(value.cost.estimated_micros, 10_000_000_000)
    || value.cost.estimated_micros !== estimate
    || !isInt(value.cost.unknown_reserved_micros, 10_000_000_000)
    || !isInt(value.cost.unknown_cost_rows, 10)
    || value.cost.unknown_cost_rows !== unknownCostRows
    || value.cost.basis !== "estimated_from_reported_usage_peak_pricing"
    || value.cost.is_invoice !== false
  ) {
    return null;
  }

  return {
    ...expected,
    usage,
    cost: {
      currency: "USD",
      estimated_micros: estimate,
      unknown_reserved_micros: value.cost.unknown_reserved_micros,
      unknown_cost_rows: unknownCostRows,
      basis: "estimated_from_reported_usage_peak_pricing",
      is_invoice: false,
    },
  };
}

function parseReport(
  value: unknown,
  expectedProjectId: string,
): PilotReportIdentity | null {
  if (!exact(value, REPORT_KEYS)) return null;
  const reportId = identifier(value.report_id);
  const pilotId = identifier(value.pilot_id);
  if (reportId === null || pilotId === null) return null;
  if (
    value.project_id !== MINBAR_PILOT_PROJECT_ID
    || value.project_id !== expectedProjectId
    || value.project_slug !== "minbarlive-growth"
  ) {
    return null;
  }
  if (!exact(value.origin, ORIGIN_KEYS)) return null;
  if (
    value.origin.kind !== "imported_cli_pilot"
    || value.origin.live_job !== false
    || value.origin.new_run !== false
    || value.origin.code_commit !== MINBAR_PILOT_CODE_COMMIT
  ) {
    return null;
  }
  const digests: string[] = [];
  for (const key of ["input_sha256", "state_sha256", "rows_sha256", "result_sha256"]) {
    const parsed = digest(value.origin[key]);
    if (parsed === null) return null;
    digests.push(parsed);
  }
  if (!exact(value.view, VIEW_KEYS)) return null;
  if (
    value.view.implementation !== MINBAR_PILOT_VIEW_IMPLEMENTATION
    || value.view.schema_version !== MINBAR_PILOT_SCHEMA_VERSION
  ) {
    return null;
  }
  return {
    report_id: reportId,
    pilot_id: pilotId,
    project_id: MINBAR_PILOT_PROJECT_ID,
    project_slug: "minbarlive-growth",
    origin: {
      kind: "imported_cli_pilot",
      live_job: false,
      new_run: false,
      code_commit: MINBAR_PILOT_CODE_COMMIT,
      input_sha256: digests[0],
      state_sha256: digests[1],
      rows_sha256: digests[2],
      result_sha256: digests[3],
    },
    view: {
      implementation: MINBAR_PILOT_VIEW_IMPLEMENTATION,
      schema_version: MINBAR_PILOT_SCHEMA_VERSION,
    },
  };
}

export function parsePilotResult(
  raw: unknown,
  options: { projectId: string },
): PilotResult | null {
  if (exact(raw, ["schema_version", "state", "reason"])) {
    if (
      raw.schema_version === MINBAR_PILOT_SCHEMA_VERSION
      && raw.state === "unavailable"
      && raw.reason === "not_configured"
    ) {
      return {
        schema_version: MINBAR_PILOT_SCHEMA_VERSION,
        state: "unavailable",
        reason: "not_configured",
      };
    }
    return null;
  }
  if (!exact(raw, TOP_KEYS)) return null;
  if (
    raw.schema_version !== MINBAR_PILOT_SCHEMA_VERSION
    || raw.state !== "available"
  ) {
    return null;
  }
  const report = parseReport(raw.report, options.projectId);
  if (report === null) return null;

  if (!exact(raw.timing, TIMING_KEYS)) return null;
  const finished = timestamp(raw.timing.pilot_finished_at);
  const prepared = timestamp(raw.timing.view_prepared_at);
  if (finished === null || prepared === null || Date.parse(prepared) < Date.parse(finished)) {
    return null;
  }

  if (!exact(raw.model, MODEL_KEYS)) return null;
  if (raw.model.requested !== MINBAR_PILOT_REQUESTED_MODEL) return null;
  const returned = list(raw.model.returned, 2);
  if (returned === null || returned.length === 0) return null;
  if (returned.some((item) =>
    typeof item !== "string" || !ALLOWED_RETURNED_MODELS.has(item))) {
    return null;
  }
  const returnedStrings = returned as string[];
  if (new Set(returnedStrings).size !== returnedStrings.length) return null;
  if (returnedStrings.slice().sort().join("|") !== returnedStrings.join("|")) return null;

  if (!exact(raw.human_acceptance, ACCEPTANCE_KEYS)) return null;
  if (
    raw.human_acceptance.state !== "pending"
    || raw.human_acceptance.accepted_means !== "schema_and_source_citation_checks_only"
  ) {
    return null;
  }

  if (!exact(raw.provenance, PROVENANCE_KEYS)) return null;
  if (
    raw.provenance.kind !== "parent_curated_organizational_summary"
    || raw.provenance.source !== "existing_pilot_responses"
  ) {
    return null;
  }
  const notice = text(raw.provenance.notice, 1000);
  const storedOmitted = nullableInt(raw.provenance.omitted_content_count, 100_000);
  if (notice === null || storedOmitted === null) return null;

  const rawRows = list(raw.rows, 10);
  if (rawRows === null || rawRows.length !== 10) return null;
  const rows: PilotRow[] = [];
  for (let index = 0; index < rawRows.length; index += 1) {
    const row = parseRow(rawRows[index], index + 2);
    if (row === null) return null;
    rows.push(row);
  }
  if (new Set(rows.map((row) => row.row_id)).size !== rows.length) return null;
  if (new Set(rows.map((row) => row.organization_id)).size !== rows.length) return null;
  const sourceIds = rows.flatMap((row) => row.evidence.pages.map((page) => page.source_id));
  if (new Set(sourceIds).size !== sourceIds.length) return null;
  const actualModels = new Set(
    rows.map((row) => row.call.actual_model).filter((item): item is string => item !== null),
  );
  if (
    actualModels.size !== returnedStrings.length
    || returnedStrings.some((item) => !actualModels.has(item))
  ) {
    return null;
  }

  const omittedTotal = rows.reduce(
    (total, row) => total + row.presentation.omitted_content_count,
    0,
  );
  if (storedOmitted !== omittedTotal) return null;
  const summary = parseSummary(raw.summary, rows, omittedTotal);
  if (summary === null) return null;
  const limitations = stringList(raw.limitations, 12, 500);
  if (limitations === null) return null;

  return {
    schema_version: MINBAR_PILOT_SCHEMA_VERSION,
    state: "available",
    report,
    timing: {
      pilot_finished_at: finished,
      view_prepared_at: prepared,
    },
    model: {
      requested: MINBAR_PILOT_REQUESTED_MODEL,
      returned: returnedStrings,
    },
    human_acceptance: {
      state: "pending",
      accepted_means: "schema_and_source_citation_checks_only",
    },
    provenance: {
      kind: "parent_curated_organizational_summary",
      source: "existing_pilot_responses",
      omitted_content_count: omittedTotal,
      notice,
    },
    summary,
    rows,
    limitations,
  };
}
